/**
 * @file Server-side validation for the calendar library.
 * These validations can include database checks and are more comprehensive than client-side validation.
 */

import { DataSource } from 'typeorm';
import { Event, Calendar, Venue, Contact } from './database/entities';
import { isValidEmail, isValidPhone } from '../shared/utils';
import { EVENT_TYPES, PAYMENT_STATUS, EVENT_STATUS } from '../shared/enums';

export interface ServerValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
  warnings?: Record<string, string[]>;
}

/**
 * Validates event data with database checks.
 */
export async function validateEventWithDb(
  data: Partial<Event>, 
  dataSource: DataSource,
  eventId?: string
): Promise<ServerValidationResult> {
  const errors: Record<string, string[]> = {};
  const warnings: Record<string, string[]> = {};

  // Basic validation (same as client-side)
  if (!data.summary || data.summary.trim().length === 0) {
    errors.summary = ['Event title is required'];
  } else if (data.summary.length > 200) {
    errors.summary = ['Event title must not exceed 200 characters'];
  }

  if (!data.start) {
    errors.start = ['Start date/time is required'];
  }

  if (!data.end) {
    errors.end = ['End date/time is required'];
  }

  if (data.start && data.end) {
    if (data.start >= data.end) {
      errors.end = ['End time must be after start time'];
    }
    
    const durationHours = (data.end.getTime() - data.start.getTime()) / (1000 * 60 * 60);
    if (durationHours > 24) {
      errors.end = ['Event duration cannot exceed 24 hours'];
    }
  }

  if (!data.type) {
    errors.type = ['Event type is required'];
  } else if (!Object.values(EVENT_TYPES).includes(data.type as any)) {
    errors.type = ['Invalid event type'];
  }

  // Database-specific validations
  if (data.calendar) {
    const calendarRepo = dataSource.getRepository(Calendar);
    const calendar = await calendarRepo.findOneBy({ id: data.calendar.id });
    if (!calendar) {
      errors.calendar = ['Selected calendar does not exist'];
    }
  }

  if (data.venue) {
    const venueRepo = dataSource.getRepository(Venue);
    const venue = await venueRepo.findOneBy({ id: data.venue.id });
    if (!venue) {
      errors.venue = ['Selected venue does not exist'];
    }
  }

  if (data.primaryContact) {
    const contactRepo = dataSource.getRepository(Contact);
    const contact = await contactRepo.findOneBy({ id: data.primaryContact.id });
    if (!contact) {
      errors.primaryContact = ['Selected contact does not exist'];
    }
  }

  // Check for overlapping events (warning, not error)
  if (data.start && data.end && data.calendar) {
    const eventRepo = dataSource.getRepository(Event);
    const overlappingEvents = await eventRepo
      .createQueryBuilder('event')
      .where('event.calendarId = :calendarId', { calendarId: data.calendar.id })
      .andWhere('event.start < :end', { end: data.end })
      .andWhere('event.end > :start', { start: data.start })
      .andWhere(eventId ? 'event.id != :eventId' : '1=1', { eventId })
      .getMany();

    if (overlappingEvents.length > 0) {
      warnings.schedule = [`This event overlaps with ${overlappingEvents.length} other event(s)`];
    }
  }

  // Musician-specific business logic validations
  if (data.type === EVENT_TYPES.LESSON) {
    if (!data.primaryContact) {
      errors.primaryContact = ['Student contact is required for lessons'];
    }
    
    if (!data.studentLevel) {
      warnings.studentLevel = ['Student level is recommended for lessons'];
    }
  }

  if (data.type === EVENT_TYPES.GIG) {
    if (!data.venue) {
      warnings.venue = ['Venue is recommended for gigs'];
    }
    
    if (!data.paymentStatus) {
      warnings.paymentStatus = ['Payment status is recommended for gigs'];
    }
  }

  if (data.paymentStatus === PAYMENT_STATUS.OVERDUE && data.paymentDueDate) {
    const now = new Date();
    if (data.paymentDueDate > now) {
      errors.paymentStatus = ['Payment cannot be overdue if due date is in the future'];
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings: Object.keys(warnings).length > 0 ? warnings : undefined
  };
}

/**
 * Validates calendar data with database checks.
 */
export async function validateCalendarWithDb(
  data: Partial<Calendar>,
  dataSource: DataSource,
  calendarId?: string
): Promise<ServerValidationResult> {
  const errors: Record<string, string[]> = {};

  if (!data.name || data.name.trim().length === 0) {
    errors.name = ['Calendar name is required'];
  } else if (data.name.length > 100) {
    errors.name = ['Calendar name must not exceed 100 characters'];
  }

  if (data.description && data.description.length > 500) {
    errors.description = ['Description must not exceed 500 characters'];
  }

  if (!data.type) {
    errors.type = ['Calendar type is required'];
  } else if (!['individual', 'group'].includes(data.type)) {
    errors.type = ['Calendar type must be either "individual" or "group"'];
  }

  // Check for duplicate calendar names (warning)
  if (data.name) {
    const calendarRepo = dataSource.getRepository(Calendar);
    const existingCalendar = await calendarRepo
      .createQueryBuilder('calendar')
      .where('LOWER(calendar.name) = LOWER(:name)', { name: data.name })
      .andWhere(calendarId ? 'calendar.id != :calendarId' : '1=1', { calendarId })
      .getOne();

    if (existingCalendar) {
      errors.name = ['A calendar with this name already exists'];
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validates venue data with database checks.
 */
export async function validateVenueWithDb(
  data: Partial<Venue>,
  dataSource: DataSource,
  venueId?: string
): Promise<ServerValidationResult> {
  const errors: Record<string, string[]> = {};

  if (!data.name || data.name.trim().length === 0) {
    errors.name = ['Venue name is required'];
  } else if (data.name.length > 200) {
    errors.name = ['Venue name must not exceed 200 characters'];
  }

  if (data.contactEmail && !isValidEmail(data.contactEmail)) {
    errors.contactEmail = ['Contact email must be a valid email address'];
  }

  if (data.contactPhone && !isValidPhone(data.contactPhone)) {
    errors.contactPhone = ['Contact phone must be a valid Australian phone number'];
  }

  if (data.website && !/^https?:\/\/.+/.test(data.website)) {
    errors.website = ['Website must be a valid URL starting with http:// or https://'];
  }

  // Check for duplicate venue names in the same city (warning)
  if (data.name && data.city) {
    const venueRepo = dataSource.getRepository(Venue);
    const existingVenue = await venueRepo
      .createQueryBuilder('venue')
      .where('LOWER(venue.name) = LOWER(:name)', { name: data.name })
      .andWhere('LOWER(venue.city) = LOWER(:city)', { city: data.city })
      .andWhere(venueId ? 'venue.id != :venueId' : '1=1', { venueId })
      .getOne();

    if (existingVenue) {
      errors.name = ['A venue with this name already exists in this city'];
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validates contact data with database checks.
 */
export async function validateContactWithDb(
  data: Partial<Contact>,
  dataSource: DataSource,
  contactId?: string
): Promise<ServerValidationResult> {
  const errors: Record<string, string[]> = {};

  if (!data.name || data.name.trim().length === 0) {
    errors.name = ['Contact name is required'];
  } else if (data.name.length > 200) {
    errors.name = ['Contact name must not exceed 200 characters'];
  }

  if (data.email && !isValidEmail(data.email)) {
    errors.email = ['Email must be a valid email address'];
  }

  if (data.phone && !isValidPhone(data.phone)) {
    errors.phone = ['Phone must be a valid Australian phone number'];
  }

  // Check for duplicate email addresses (error)
  if (data.email) {
    const contactRepo = dataSource.getRepository(Contact);
    const existingContact = await contactRepo
      .createQueryBuilder('contact')
      .where('LOWER(contact.email) = LOWER(:email)', { email: data.email })
      .andWhere(contactId ? 'contact.id != :contactId' : '1=1', { contactId })
      .getOne();

    if (existingContact) {
      errors.email = ['A contact with this email address already exists'];
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validates business rules for event scheduling.
 */
export async function validateEventScheduling(
  eventData: Partial<Event>,
  dataSource: DataSource
): Promise<ServerValidationResult> {
  const errors: Record<string, string[]> = {};
  const warnings: Record<string, string[]> = {};

  if (!eventData.start || !eventData.end || !eventData.calendar) {
    return { isValid: true, errors: {} }; // Skip if basic data is missing
  }

  const eventRepo = dataSource.getRepository(Event);

  // Check for double-booking (same time, same calendar)
  const conflictingEvents = await eventRepo
    .createQueryBuilder('event')
    .where('event.calendarId = :calendarId', { calendarId: eventData.calendar.id })
    .andWhere('event.start < :end', { end: eventData.end })
    .andWhere('event.end > :start', { start: eventData.start })
    .getMany();

  if (conflictingEvents.length > 0) {
    errors.schedule = ['This time slot conflicts with existing events'];
  }

  // Check for reasonable scheduling patterns
  const dayOfWeek = eventData.start.getDay();
  const hour = eventData.start.getHours();

  // Warn about unusual scheduling
  if (eventData.type === EVENT_TYPES.LESSON) {
    if (dayOfWeek === 0 || dayOfWeek === 6) { // Weekend
      warnings.schedule = ['Weekend lessons are unusual - please confirm this is correct'];
    }
    
    if (hour < 8 || hour > 20) { // Very early or late
      warnings.schedule = ['Lessons outside 8am-8pm are unusual - please confirm this is correct'];
    }
  }

  if (eventData.type === EVENT_TYPES.GIG) {
    if (hour < 10 || hour > 23) { // Very early or very late
      warnings.schedule = ['Gig times outside 10am-11pm are unusual - please confirm this is correct'];
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings: Object.keys(warnings).length > 0 ? warnings : undefined
  };
} 